/**
 * Resolve pending AI suggestions entirely client-side.
 *
 * AI proposals are typed `pre_order_suggestions` / `pre_order_flow_suggestions`
 * rows (modify/delete against committed rows) plus AI-created rows in the main
 * table at `status = AI_PROPOSAL` (adds). The recap projects those rows into
 * the legacy `ai_suggestions[]` block shape (`aiSuggestionProjection.ts`) so
 * this resolver — and every render reader — keeps reading blocks off
 * `pre_order_metadata` / `flow_metadata`. On resolve it produces TWO outputs:
 *
 *  1. the merged `ModifyBatchInput` (column writes + parent status transition
 *     + soft-deletes) shipped via ONE push-batch — backend is a generic CRUD
 *     endpoint that applies whatever the client sends;
 *  2. `resolutions` — the set of sidecar ROWS to stamp `accepted_at` /
 *     `declined_at`, applied by the caller through the suggestion `resolve`
 *     mutations. The row is the durable source of truth; the
 *     `ai_suggestions[]` block the merge writes into metadata is vestigial
 *     and stripped before the batch ships.
 *
 * Why frontend-side:
 *  - Single source of authority for AI semantics, co-located with the
 *    UI that displays them. No drift between server and client logic.
 *  - True offline-first: the merged state is computed locally and
 *    queued via the existing offline-first push-batch when the network
 *    is down. The user sees the resolved state instantly.
 *  - Backend `push-batch-service` shrinks ~250 LOC.
 *
 * Per-block semantics (same as the deleted backend version):
 *
 * Accept path:
 *   - `type:modify` — apply AI's `to` per field; if user touched a
 *     field, user wins (block stamps `modified` with `finalTo`).
 *     Equality-aware: a user value equal to AI's `to` is not an
 *     override.
 *   - `type:add` — flip `flow.status: 5 → 0` (PENDING); accept block.
 *     A user touch on a type:add row counts as `modified` only if the
 *     overridden field differs from the row's current column value
 *     (since the row already has AI's column proposals).
 *   - `type:delete` + user untouched — soft-delete the row + accept
 *     block.
 *   - `type:delete` + user editing the same row → user wants to keep
 *     it alive; block stamped `declined`, user's edit applied.
 *
 *   Parent `AI_PROPOSAL → PENDING` promotion fires when every pending
 *   block on the PO is resolved.
 *
 * Decline path:
 *   - `type:modify` / `type:delete` — flip block to `declined`; row
 *     unchanged.
 *   - `type:add` — soft-delete the AI-added row + flip block declined.
 *   - Wholesale-decline of an `AI_PROPOSAL` parent (every pending
 *     block on the PO is being declined) — soft-delete the parent
 *     itself. Recap "restore" brings the proposal back exactly as
 *     submitted.
 *
 * Audit attribution: column writes ride push-batch as
 * `actor_type=user, source=v2_push_batch`. AI lineage stays on the
 * resolved block (`proposedBy` / `resolvedBy` / `resolvedAt` /
 * `finalTo`). Nothing about provenance changes on the wire.
 */

import {
  PreOrderStatus,
  type PreOrder,
  type PreOrderFlow,
} from "@/app/api/types/preOrder";
import type { ModifyBatchInput } from "@stockline/web";
import {
  type AiSuggestionBlock,
  readBlocks,
  latestPending,
  stripAiBlocksFromBatch,
} from "./helpers";
import { mergeUserChangeWithAi, synthesizeAiOnlyFlow } from "./flow";
import { mergeHeaderAi } from "./header";

export interface AiResolveContext {
  userId: string;
  resolvedAt: string;
}

export type AiDecision = "accept" | "decline";

/** A sidecar suggestion ROW to stamp after the push-batch lands. Synthetic
 * add blocks (AI-created flows, no row) carry `id: null` and resolve through
 * the main-row write instead. */
export interface SuggestionResolution {
  kind: "header" | "flow";
  /** Sidecar row id, or null for a status-based add (no row). */
  id: string | null;
  decision: "accepted" | "declined";
}

/**
 * Looks up the sidecar ROW id for a committed target, so the resolver can
 * report which rows to stamp. Built by the recap bridge from the typed-row
 * maps (`buildSuggestionMaps`). Omitted by callers that don't yet supply
 * typed rows (legacy dev queue) — then `resolutions` is empty.
 */
export interface SuggestionRowIndex {
  header: Map<string, string[]>;
  flow: Map<string, string[]>;
}

export const EMPTY_ROW_INDEX: SuggestionRowIndex = {
  header: new Map(),
  flow: new Map(),
};

export interface AiResolveResult {
  /** Final ModifyBatchInput ready for `pushBatch`. */
  batch: ModifyBatchInput;
  /**
   * `true` when the resolved payload would leave the pre-order itself
   * tombstoned (wholesale-decline of an AI_PROPOSAL parent). Callers
   * can use this to short-circuit local UI navigation.
   */
  parentSoftDeleted: boolean;
  /** Sidecar rows to stamp accepted/declined after push-batch succeeds. */
  resolutions: SuggestionResolution[];
}

/**
 * Take a base `ModifyBatchInput` produced from the modify buffer
 * (`buildModifyBatchInput`) and enrich it with the local resolution of
 * every pending AI block on `preOrder`. The returned batch is what
 * gets shipped to push-batch — backend just applies what the client
 * sends.
 *
 * Returns the batch unchanged when no pending blocks exist on the PO.
 *
 * When `decision === "decline"` and every pending block is being
 * declined on an `AI_PROPOSAL` parent, the returned batch soft-deletes
 * the parent (wholesale-decline gesture). `parentSoftDeleted` is
 * surfaced so callers can skip post-success navigation.
 */
export function resolveAiLocally(
  ctx: AiResolveContext,
  preOrder: PreOrder,
  base: ModifyBatchInput,
  decision: AiDecision,
  rowIndex: SuggestionRowIndex,
): AiResolveResult {
  const headerMetadata = preOrder.pre_order_metadata ?? null;
  const headerBlocks = readBlocks(headerMetadata);
  const pendingHeader = latestPending(headerBlocks);
  const pendingFlows = collectPendingFlows(preOrder);

  if (!pendingHeader && pendingFlows.length === 0) {
    // No per-block resolution to do. For a clean PO the batch passes
    // through unchanged. For a status=AI_PROPOSAL parent the gesture
    // still has meaning: the row itself is the proposal, so accept
    // promotes the parent to PENDING and decline soft-deletes it
    // (wholesale-decline). Covers stuck-state rows whose header block
    // was never written (legacy / failed side-effect) or whose blocks
    // were already resolved without promoting the parent.
    if (preOrder.status !== PreOrderStatus.AI_PROPOSAL) {
      return { batch: base, parentSoftDeleted: false, resolutions: [] };
    }
    if (decision === "decline") return wholesaleDecline(base);
    return {
      batch: {
        ...base,
        preOrderUpdate: { ...base.preOrderUpdate, status: 0 },
      },
      parentSoftDeleted: false,
      resolutions: [],
    };
  }

  if (
    decision === "decline" &&
    preOrder.status === PreOrderStatus.AI_PROPOSAL
  ) {
    // Wholesale-decline tombstones the parent; the sidecar rows ride along
    // (the recap "restore" un-deletes the parent and its rows together), so
    // no per-row stamp is emitted here.
    return wholesaleDecline(base);
  }

  const resolutions = collectResolutions(
    preOrder,
    pendingHeader,
    pendingFlows,
    base.flowChanges,
    decision,
    rowIndex,
  );

  const enriched = mergeFlowChanges(
    ctx,
    base.flowChanges,
    pendingFlows,
    decision,
  );
  const preOrderUpdate = mergeHeader({
    ctx,
    preOrder,
    headerMetadata,
    headerBlocks,
    pendingHeader,
    base: base.preOrderUpdate,
    decision,
    promoteParent:
      preOrder.status === PreOrderStatus.AI_PROPOSAL && decision === "accept",
  });

  // The row is the durable source of truth — strip the vestigial
  // `ai_suggestions[]` the merge wrote into the metadata blobs so we don't
  // re-seed the legacy JSON path the projection replaced.
  const batch = stripAiBlocksFromBatch({
    ...base,
    flowChanges: enriched,
    preOrderUpdate,
  });

  return { batch, parentSoftDeleted: false, resolutions };
}

/**
 * Derive the sidecar-row stamp list from the SAME inputs the merge consumes,
 * so the row lifecycle matches the column writes without re-reading the
 * stripped blocks. A `type:delete` block the user is editing flips to
 * `declined` even on accept (the user keeps the row alive); everything else
 * follows the gesture. Synthetic add blocks (no row) are skipped — they
 * resolve through the flow's status write.
 */
function collectResolutions(
  preOrder: PreOrder,
  pendingHeader: AiSuggestionBlock | null,
  pendingFlows: PendingFlow[],
  userChanges: ModifyBatchInput["flowChanges"],
  decision: AiDecision,
  rowIndex: SuggestionRowIndex,
): SuggestionResolution[] {
  const out: SuggestionResolution[] = [];
  if (pendingHeader) {
    // Stamp EVERY pending row merged into the block, not just one — otherwise
    // the un-stamped rows resurface as phantom pending proposals next recompute.
    for (const id of rowIndex.header.get(preOrder.id) ?? []) {
      out.push({ kind: "header", id, decision: declineOrAccept(decision) });
    }
  }
  const editedFlowIds = new Set(
    userChanges.filter((c) => c.op === "update").map((c) => c.id),
  );
  for (const { flow, pending } of pendingFlows) {
    if (pending.type === "add") continue; // synthetic, no row
    const ids = rowIndex.flow.get(flow.id) ?? [];
    if (ids.length === 0) continue;
    const keptAlive =
      decision === "accept" &&
      pending.type === "delete" &&
      editedFlowIds.has(flow.id);
    const rowDecision = keptAlive ? "declined" : declineOrAccept(decision);
    for (const id of ids) {
      out.push({ kind: "flow", id, decision: rowDecision });
    }
  }
  return out;
}

function declineOrAccept(decision: AiDecision): "accepted" | "declined" {
  return decision === "accept" ? "accepted" : "declined";
}

// ---------- helpers ----------

interface PendingFlow {
  flow: PreOrderFlow;
  blocks: AiSuggestionBlock[];
  pending: AiSuggestionBlock;
}

function collectPendingFlows(preOrder: PreOrder): PendingFlow[] {
  if (!Array.isArray(preOrder.flows)) return [];
  const out: PendingFlow[] = [];
  for (const flow of preOrder.flows) {
    if ((flow as { deleted_at?: string | null }).deleted_at != null) continue;
    const blocks = readBlocks(flow.flow_metadata ?? null);
    const pending = latestPending(blocks);
    if (pending) out.push({ flow, blocks, pending });
  }
  return out;
}

/**
 * Wholesale-decline of an AI_PROPOSAL parent: soft-delete the parent
 * itself and skip per-block resolution. Recap "restore" brings the
 * proposal back as submitted.
 */
function wholesaleDecline(base: ModifyBatchInput): AiResolveResult {
  const preOrderUpdate = {
    ...base.preOrderUpdate,
    deletedAt: new Date().toISOString(),
  } as ModifyBatchInput["preOrderUpdate"];
  return {
    batch: { ...base, preOrderUpdate },
    parentSoftDeleted: true,
    resolutions: [],
  };
}

function mergeFlowChanges(
  ctx: AiResolveContext,
  userChanges: ModifyBatchInput["flowChanges"],
  pendingFlows: PendingFlow[],
  decision: AiDecision,
): ModifyBatchInput["flowChanges"] {
  const touched = new Set(userChanges.map((c) => c.id));
  const out: ModifyBatchInput["flowChanges"] = [];

  // Pass 1 — user's flow ops, enriched with any pending AI on the
  // same flow.
  for (const change of userChanges) {
    const target = pendingFlows.find((p) => p.flow.id === change.id);
    if (!target) {
      out.push(change);
      continue;
    }
    out.push(
      ...mergeUserChangeWithAi(
        ctx,
        target.flow,
        target.blocks,
        target.pending,
        change,
        decision,
      ),
    );
  }

  // Pass 2 — synthesized changes for AI-only flows (untouched by user).
  for (const { flow, blocks, pending } of pendingFlows) {
    if (touched.has(flow.id)) continue;
    out.push(...synthesizeAiOnlyFlow(ctx, flow, blocks, pending, decision));
  }
  return out;
}

interface MergeHeaderArgs {
  ctx: AiResolveContext;
  /** Live pre-order row — header edits land here via a live mutation (not
   * the modify buffer), so the accept-time merge reads the user's current
   * column/metadata values off it to detect overrides. */
  preOrder: PreOrder;
  headerMetadata: Record<string, unknown> | null;
  headerBlocks: AiSuggestionBlock[];
  pendingHeader: AiSuggestionBlock | null;
  base: ModifyBatchInput["preOrderUpdate"];
  decision: AiDecision;
  /**
   * `true` when the parent was at AI_PROPOSAL and we're accepting —
   * the resolved batch must promote `status: 0` so the queue stops
   * surfacing the row.
   */
  promoteParent: boolean;
}

function mergeHeader(
  args: MergeHeaderArgs,
): ModifyBatchInput["preOrderUpdate"] {
  const {
    ctx,
    preOrder,
    headerMetadata,
    headerBlocks,
    pendingHeader,
    base,
    decision,
    promoteParent,
  } = args;
  let out = pendingHeader
    ? mergeHeaderAi(
        ctx,
        headerMetadata,
        headerBlocks,
        pendingHeader,
        base,
        decision,
        preOrder,
      )
    : base;
  if (promoteParent) {
    out = { ...out, status: 0 };
  }
  return out;
}
